import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useState, useRef, useLayoutEffect } from 'react';
import { Card } from '@/components/ui/card';
import { ActivityCard } from './ActivityCard';
import { Pagination } from './Pagination';
import {
  activitiesPaginationAtom,
  loadActivitiesPageAtom,
  goToNextPageAtom,
  goToPreviousPageAtom,
  goToPageAtom,
  resetActivitiesPaginationAtom,
} from '@/features/activity/atoms';
import type { Activity } from '@/features/activity/types';
import { selectedClientIdAtom } from '@/state/clients';
import { TokenService } from '@/services/auth/tokenService';

interface ActivitiesListProps {
  onActivityClick?: (activity: Activity) => void;
  className?: string;
}

export function ActivitiesList({
  className = '',
}: ActivitiesListProps) {
  const [activities, setActivities] = useState<Activity[]>([]);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const previousClientId = useRef<string | null>(null);
  const selectedClientId = useAtomValue(selectedClientIdAtom);
  const pagination = useAtomValue(activitiesPaginationAtom);
  const loadPage = useSetAtom(loadActivitiesPageAtom);
  const loadPageRef = useRef(loadPage);
  const goToNextPage = useSetAtom(goToNextPageAtom);
  const goToPreviousPage = useSetAtom(goToPreviousPageAtom);
  const goToPage = useSetAtom(goToPageAtom);
  const resetPagination = useSetAtom(resetActivitiesPaginationAtom);

  // Update ref when loadPage changes
  loadPageRef.current = loadPage;

  // Reset pagination immediately when clientId changes
  useLayoutEffect(() => {
    if (selectedClientId && selectedClientId !== previousClientId.current) {
      // Reset pagination state synchronously
      resetPagination();
      setActivities([]);
      previousClientId.current = selectedClientId;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientId]);

  // Load activities for the current page
  useEffect(() => {
    const loadActivities = async () => {
      const tokenService = TokenService.getInstance();
      const authToken = await tokenService.getCurrentToken();

      if (!authToken || !selectedClientId) return;

      try {
        const response = await loadPageRef.current({
          clientId: selectedClientId,
          page: pagination.currentPage,
          limit: pagination.pageSize,
        });

        if (response?.activities) {
          setActivities(response.activities);
        }
      } catch (error) {
        console.error('Failed to load activities:', error);
        setActivities([]);
      }
    };

    loadActivities();
  }, [selectedClientId, pagination.currentPage, pagination.pageSize]);

  const scrollToTop = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  };

  const handleNextPage = async () => {
    if (!selectedClientId) return;
    try {
      const response = await goToNextPage(selectedClientId);
      if (response?.activities) {
        setActivities(response.activities);
        scrollToTop();
      }
    } catch (error) {
      console.error('Failed to load next page:', error);
    }
  };

  const handlePreviousPage = async () => {
    if (!selectedClientId) return;
    try {
      const response = await goToPreviousPage(selectedClientId);
      if (response?.activities) {
        setActivities(response.activities);
        scrollToTop();
      }
    } catch (error) {
      console.error('Failed to load previous page:', error);
    }
  };

  const handleGoToPage = async (page: number) => {
    if (!selectedClientId) return;
    try {
      const response = await goToPage({ clientId: selectedClientId, page });
      if (response?.activities) {
        setActivities(response.activities);
        scrollToTop();
      }
    } catch (error) {
      console.error('Failed to load page:', error);
    }
  };

  return (
    <div className={`flex flex-col h-full ${className}`}>
      {/* Scrollable Content Area */}
      <div ref={scrollContainerRef} className="flex-1 overflow-auto pb-16">
        {/* Activities List */}
        {activities.length > 0 && (
          <div className="rounded-xs overflow-hidden">
            {activities.map((activity) => (
              <ActivityCard
                key={activity.activityId}
                activity={activity}
              />
            ))}
          </div>
        )}

        {/* Empty state */}
        {activities.length === 0 && !pagination.loading && (
          <Card className="p-6 text-center">
            <div className="text-gray-500">
              <p className="text-lg font-medium mb-2">No activities found</p>
              <p className="text-sm">
                No activities have been recorded for this client yet.
              </p>
            </div>
          </Card>
        )}
      </div>

      {/* Fixed Pagination Controls at Bottom - Only show if there are activities */}
      {activities.length > 0 && (
        <div className="sticky bottom-0 bg-inherit border-t border-gray-200 py-1">
          <Pagination
            key={selectedClientId}
            currentPage={pagination.currentPage}
            hasNextPage={pagination.hasNextPage}
            loading={pagination.loading}
            onPreviousPage={handlePreviousPage}
            onNextPage={handleNextPage}
            onGoToPage={handleGoToPage}
          />
        </div>
      )}
    </div>
  );
}
